// Mobile route registry — used by the global mobile top bar/drawer and
// the desktop-only gate. Kept as plain data so both `MobileTopBarHost`
// and route wrappers stay in sync.
//
// Non-technician mobile users see:
//   • A dedicated mobile layout on every route in MOBILE_ELIGIBLE
//   • A "please use a desktop" screen on every route in DESKTOP_ONLY
//
// Technicians retain their existing /mobile/* surface (untouched).

import {
  LayoutDashboard,
  MessageSquare,
  Inbox,
  Briefcase,
  CheckSquare,
  CalendarDays,
  Users,
  FileText,
  KanbanSquare,
  Package,
  HardHat,
  CreditCard,
  Settings,
  CalendarRange,
  BarChart3,
  Megaphone,
  Zap,
  Workflow,
  Bot,
  PhoneCall,
  Clock,
} from "lucide-react";

// 15 pages that get a full mobile redesign.
// `path` matches an exact pathname; `titles` are used by the mobile top bar
// so the header shows the right label even when we haven't redesigned that
// page yet. An optional `module` gates the item on the company's plan —
// always visible in the drawer, but greyed out + non-clickable when the
// plan doesn't include it (see MobileAppDrawer.jsx).
// `roles` (optional) mirrors the matching route's RoleRoute in App.js —
// omit it for pages every logged-in role may open. The drawer hides any
// item the current user's role isn't listed in.
export const MOBILE_ELIGIBLE = [
  { path: "/dashboard", title: "Dashboard", icon: LayoutDashboard },
  { path: "/messages", title: "Messages", icon: MessageSquare },
  { path: "/inbox", title: "Inbox", icon: Inbox, roles: ["admin", "dispatcher"] },
  { path: "/jobs", title: "Jobs", icon: Briefcase, roles: ["admin", "dispatcher", "technician"] },
  { path: "/tasks", title: "Tasks", icon: CheckSquare, roles: ["admin", "dispatcher", "technician"] },
  { path: "/calendar", title: "Calendar", icon: CalendarDays, roles: ["admin", "dispatcher", "technician", "office_personnel"] },
  { path: "/customers", title: "Customers", icon: Users, roles: ["admin", "dispatcher", "technician"] },
  { path: "/history", title: "Job History", icon: Clock, roles: ["admin", "dispatcher", "office_personnel", "technician"] },
  { path: "/estimates", title: "Estimates & Invoices", icon: FileText, roles: ["admin", "dispatcher", "technician"] },
  { path: "/pipeline", title: "Pipeline", icon: KanbanSquare, roles: ["admin", "dispatcher", "technician", "office_personnel"] },
  { path: "/pricebook", title: "Pricebook & Inventory", icon: Package, roles: ["admin", "dispatcher", "technician"] },
  { path: "/mobile/guru", title: "Diagnostic Guru", icon: Bot, module: "guru", roles: ["admin", "dispatcher", "technician", "office_personnel"] },
  { path: "/technicians", title: "Staff", icon: HardHat, roles: ["admin", "dispatcher"] },
  { path: "/dispatch", title: "Dispatch Board", icon: CalendarRange, roles: ["admin", "dispatcher"] },
  { path: "/settings/billing", title: "Billing & Subscription", icon: CreditCard, roles: ["admin"] },
  { path: "/settings/company", title: "Company Settings", icon: Settings, roles: ["admin"] },
];

// Pages that are deliberately desktop-only on mobile. The drawer lists
// them in a dimmed "Desktop Only" section, and visiting them on mobile
// shows the DesktopOnlyScreen.
export const DESKTOP_ONLY = [
  { path: "/reports", title: "Reports", icon: BarChart3 },
  { path: "/marketing", title: "Marketing", icon: Megaphone },
  { path: "/automations", title: "Automations", icon: Zap },
  { path: "/flowchart", title: "Flow Chart", icon: Workflow },
  { path: "/ai-agents", title: "AI Voice Agents", icon: PhoneCall },
];

// Routes where the mobile shell (top bar + drawer + bottom nav) MUST NOT
// render — full-screen sub-pages with their own back-button header, public
// marketing/auth/payment/form pages, and the platform super-admin app.
// `/mobile` itself (the shared Home surface) is NOT hidden — every role
// gets the same top bar + drawer there.
const HIDE_EXACT = new Set([
  "/",
  "/login",
  "/register",
  "/signup",
  "/pricing",
  "/forgot-password",
  "/reset-password",
  "/accept-invite",
  "/auth/callback",
]);
const HIDE_PREFIX = ["/mobile/jobs", "/mobile/guru", "/mobile/calendar", "/mobile/activity", "/platform", "/pay/", "/accept/", "/f/", "/voice-commands"];

export function isMobileShellRoute(pathname) {
  if (HIDE_EXACT.has(pathname)) return false;
  if (HIDE_PREFIX.some((p) => pathname === p || pathname.startsWith(p + "/") || pathname.startsWith(p))) return false;
  return true;
}

export function findMobileTitle(pathname) {
  if (pathname === "/mobile") return "Dashboard";
  const m = MOBILE_ELIGIBLE.find((r) => r.path === pathname || pathname.startsWith(r.path + "/"));
  if (m) return m.title;
  const d = DESKTOP_ONLY.find((r) => r.path === pathname || pathname.startsWith(r.path + "/"));
  if (d) return d.title;
  return "";
}

export function isDesktopOnly(pathname) {
  return DESKTOP_ONLY.some((r) => r.path === pathname || pathname.startsWith(r.path + "/"));
}
